import api from './api';

/**
 * Service for authentication-related API calls
 */
class AuthService {
  /**
   * Login with email and password
   * 
   * @param {Object} loginRequest - Login request data
   * @param {string} loginRequest.email - User email
   * @param {string} loginRequest.password - User password
   * @param {boolean} loginRequest.rememberMe - Remember user
   * @param {string} loginRequest.captchaToken - CAPTCHA token if required
   * @returns {Promise} - Promise with login response
   */
  login(loginRequest) {
    return api.post(`/v1/auth/login`, loginRequest);
  }

  /**
   * Refresh the access token using a refresh token
   * 
   * @param {Object} refreshTokenRequest - Refresh token request data
   * @param {string} refreshTokenRequest.refreshToken - The refresh token
   * @param {boolean} refreshTokenRequest.rememberMe - Remember user
   * @returns {Promise} - Promise with the new tokens
   */
  refreshToken(refreshTokenRequest) {
    return api.post(`/v1/auth/refresh-token`, refreshTokenRequest);
  }

  /**
   * Logout the user
   * 
   * @param {boolean} allSessions - Whether to logout from all sessions
   * @returns {Promise} - Promise with logout result
   */
  logout(allSessions = false) {
    return api.post(`/v1/auth/logout`, { allSessions });
  }

  /**
   * Check if CAPTCHA is required for login
   * 
   * @param {string} email - The email to check
   * @returns {Promise} - Promise with CAPTCHA requirement info
   */
  checkCaptchaRequired(email) {
    return api.get(`/v1/auth/captcha-required`, {
      params: { email }
    }).then(response => response.data);
  }

  /**
   * Check account lock status and remaining time
   * 
   * @param {string} email - The email to check
   * @returns {Promise} - Promise with lock time info
   */
  checkAccountLockTime(email) {
    return api.get(`/v1/auth/account-lock-time`, {
      params: { email }
    }).then(response => response.data);
  }

  /**
   * Get hCaptcha site key from backend
   * 
   * @returns {Promise} - Promise with site key
   */
  getCaptchaSiteKey() {
    return api.get(`/v1/auth/captcha-site-key`).then(response => response.data.siteKey);
  }
}

export default new AuthService();